import React from "react";
import styled from "styled-components";

import { ObserveState } from "../../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../../rule-engine-core/Common/StateManager";
import { ExpressionView } from "../../../../Expressions/ExpressionView";

import { ValueRuleColumn } from "../../../../rule-engine-core/Rule/Table/Value/Structure/ValueRuleColumn";
import { ValueTableConfiguration } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableConfiguration";
import { ValueTableRuleEditContext } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableRuleEditContext";

import { ActionWrapper, EditColumnAction, EditColumnWrapper, StyledDataTypeRender } from "../../CommonStyles";
import { DeleteGrayIcon } from "../../../../CommonUI/Icon/DeleteGrayIcon";

import { Error } from "../../CommonUI/Error";
import { DataTypePicker } from "../../../../CommonUI/DataTypePicker/DataTypePicker";
import { IDataDefinition } from "../../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { TestContext } from "../../../../CommonUI/Testing/TestContext";
import { DataUtilities } from "../../../../rule-engine-core/Common/DataUtilities";
import { UIStyleConstants } from "../../../../rule-engine-core/Common/UIStyleConstants";

interface IValueTableRuleColumnRender {
    ruleDefinition: ValueTableConfiguration;
    editContext: ValueTableRuleEditContext;
    ruleColumn: ValueRuleColumn;
}

export const XValueTableRuleColumnHeader = (props: IValueTableRuleColumnRender) => {
    const { ruleColumn, editContext, ruleDefinition } = props;
    const testContext = React.useContext(TestContext);

    const onRemove = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        if (testContext) return;

        if (ruleColumn.expression.isEmpty || DataUtilities.getCookie("VALUE_RULE_DELETE_CONFIRM") === "true") {
            ruleDefinition.removeColumn(ruleColumn._id);
            StateManager.propertyChanged(ruleDefinition, "columns");
        } else {
            editContext.deletingColumn = ruleColumn;
        }
    };

    const onClickSetVariableDialog = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        editContext.selectedColForVariable = ruleColumn;
    };

    const onClickSetRuleDialog = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        editContext.selectedColForRule = ruleColumn;
    };

    return (
        <RuleTableHeaderDiv>
            <ObserveState
                listenTo={ruleColumn}
                properties={["columnName", "getRule", "cellData", "expression"]}
                control={() => {
                    let validationIssues = ruleColumn.getDesignValidationIssues();
                    return (
                        <>
                            <ValueTableRuleColumnRender onClick={onClickSetVariableDialog}>
                                <ExpressionRender onClick={onClickSetRuleDialog}>
                                    <TestContext.Provider value={undefined}>
                                        <ExpressionView
                                            expression={ruleColumn.expression}
                                            color={{
                                                expression: UIStyleConstants.BLUE_COLOR,
                                                value: UIStyleConstants.GRAY_DARK_COLOR,
                                                variable: UIStyleConstants.GRAY_DARK_COLOR,
                                                missing: UIStyleConstants.GREEN_LIGHT_COLOR,
                                            }}
                                            hideValidation
                                            onDone={() => {
                                                StateManager.propertyChanged(ruleColumn, "expression");
                                            }}
                                        />
                                    </TestContext.Provider>
                                </ExpressionRender>

                                <Error issues={validationIssues} />

                                <EditColumnWrapper className="edit-wrapper">
                                    {ruleDefinition.columns.length > 1 && !testContext ? (
                                        <EditColumnAction onClick={onRemove}>
                                            <DeleteGrayIcon />
                                        </EditColumnAction>
                                    ) : null}
                                </EditColumnWrapper>
                                <ActionWrapper>
                                    {testContext ? (
                                        <>{ruleColumn.cellData.type}</>
                                    ) : (
                                        <StyledDataTypeRender>
                                            <DataTypePicker
                                                dataDef={ruleColumn.cellData}
                                                updateCallback={(type: IDataDefinition) => {
                                                    ruleDefinition.setRuleColumnType(ruleColumn, type);
                                                    StateManager.propertyChanged(ruleColumn, "cellData");
                                                }}
                                                variant={"minimal"}
                                            />
                                        </StyledDataTypeRender>
                                    )}
                                </ActionWrapper>
                            </ValueTableRuleColumnRender>
                        </>
                    );
                }}
            />
        </RuleTableHeaderDiv>
    );
};

const RuleTableHeaderDiv = styled.div`
    display: flex;
    flex-direction: row;
    position: relative;
`;

const ValueTableRuleColumnRender = styled.div`
    position: relative;
    min-width: 120px;
    min-height: 44px;

    width: 100%;
    height: 100%;

    background: ${UIStyleConstants.WHITE_COLOR};
    border: 1px solid ${UIStyleConstants.BLUE_COLOR};
    box-sizing: border-box;
    border-radius: 2px;

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    display: flex;
    flex-direction: column;
    justify-content: center;

    .edit-button,
    .edit-wrapper {
        opacity: 0;
    }

    &:hover {
        .edit-button,
        .edit-wrapper {
            opacity: 1;
        }
    }
`;

const ExpressionRender = styled.div`
    color: ${UIStyleConstants.BLUE_COLOR};
    cursor: pointer;

    padding-top: 15px;
`;
